import React from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Alert, Row, Col } from 'react-bootstrap';

const SafetyTips: React.FC = () => {
  const isMobile = useIsMobile();

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body className="p-4 p-md-5">
          <h1 className="mb-4">Safety Tips</h1>

          <Alert variant="info" className="mb-4">
            <p className="mb-0 small">
              NayaBajar provides an online platform which helps buyers & sellers connect with each other.
              However, it is always good to take precautions before conducting any transaction.
              We advise you to always use common sense, sound judgment, and trust your instincts.
            </p>
          </Alert>

          <Alert variant="warning" className="mb-4">
            <p className="fw-bold mb-0 small">
              ⚠️ NayaBajar does not offer any kind of buyer protection and is not involved in buying
              or selling activities. All ads are posted by users who are solely responsible for their
              ad content and actions.
            </p>
          </Alert>

          {/* Tip 1: Avoid Fraud & Scams */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-danger">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">1. Avoid Fraud & Scams</h2>
                <p className="mb-3">
                  Don't assume without confirmation that ad content and pictures are true and accurate.
                  Ask questions to the ad poster for more details if you have doubts. Be suspicious of:
                </p>
              </Col>
            </Row>

            <Card bg="light" className="border-0">
              <Card.Body className="p-3">
                <div className="d-flex gap-2 mb-3">
                  <span className="text-danger mt-1">✗</span>
                  <p className="small mb-0">
                    Sellers with incomplete address, unverified mobile numbers, or phone numbers only in description
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-danger mt-1">✗</span>
                  <p className="small mb-0">
                    Ads demanding advance payment before seeing the product
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-danger mt-1">✗</span>
                  <p className="small mb-0">
                    Ads promising large sums, lottery/get-rich schemes, foreign employment, visas,
                    high return investments, or opportunities that seem "too good to be true"
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-danger mt-1">✗</span>
                  <p className="small mb-0">
                    Ads trying to sell stolen goods at unusually low prices. Ask for proof of purchase
                    (bill, warranty card) when in doubt
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <span className="text-danger mt-1">✗</span>
                  <p className="small mb-0">
                    Ads selling fake, replica, or counterfeit products as original
                  </p>
                </div>
              </Card.Body>
            </Card>

            <Alert variant="warning" className="border mt-3">
              <p className="fw-bold mb-0 small">
                💡 Golden Rule: "If something sounds too good to be true, it probably is suspicious"
              </p>
            </Alert>
          </section>

          {/* Tip 2: Meet in Person */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-success">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">2. Meet In-Person at a Public Place</h2>
                <p className="mb-3">
                  You'll likely be meeting a stranger. Learn as much as you can about the person
                  you're trading with. Ask about their workplace, profession, etc. If in doubt,
                  ask to see identification like citizenship or license.
                </p>
              </Col>
            </Row>

            <Card bg="light" className="border-0">
              <Card.Body className="p-3">
                <div className="d-flex gap-2 mb-3">
                  <span className="text-success mt-1">✓</span>
                  <p className="small mb-0">
                    Always meet in public places (restaurants, offices, shops) where other people are present
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-success mt-1">✓</span>
                  <p className="small mb-0">
                    Always carry your cell phone. If possible, take a friend along
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-success mt-1">✓</span>
                  <p className="small mb-0">
                    Inform family or friends about who you're meeting and where you're going
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-success mt-1">✓</span>
                  <p className="small mb-0">
                    Avoid meeting at home or isolated places. If required, ensure you're not alone
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <span className="text-success mt-1">✓</span>
                  <p className="small mb-0">
                    If something feels suspicious, stop immediately and leave
                  </p>
                </div>
              </Card.Body>
            </Card>
          </section>

          {/* Tip 3: Inspect Product */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">3. Inspect the Product in Detail</h2>
                <p className="mb-0">
                  We recommend physically inspecting the product before making payment. Take your time
                  to check thoroughly. Don't let the seller rush you. If you lack expertise,
                  ask a friend or professional for advice.
                </p>
              </Col>
            </Row>
          </section>

          {/* Tip 4: High Value Items */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px', backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#6f42c1'}}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">4. Be Extra Careful with High-Value Items</h2>
                <p className="mb-3">
                  For expensive purchases, meet the seller first to verify the product. Only when
                  ready to buy, visit an ATM to arrange cash.
                </p>
              </Col>
            </Row>

            <Alert variant="info" className="border" style={{backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#6f42c1'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <p className="fw-bold mb-0" style={{color: '#6f42c1'}}>Use Digital Payments</p>
              </div>
              <p className="small mb-0" style={{color: '#6f42c1'}}>
                We strongly encourage using mobile wallets (eSewa, Khalti, IME Pay, Connect IPS, etc.)
                or bank transfer instead of cash. Confirm with the seller beforehand which payment
                methods they accept.
              </p>
            </Alert>
          </section>

          {/* Tip 5: Avoid Non Face-to-Face */}
          <section className="mb-5">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-warning">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">5. Try to Avoid Non Face-to-Face Transactions</h2>
                <p className="mb-3">
                  NayaBajar is about trading locally and doesn't encourage non face-to-face transactions.
                  However, if you proceed:
                </p>
              </Col>
            </Row>

            <Card bg="light" className="border-0">
              <Card.Body className="p-3">
                <div className="d-flex gap-2 mb-3">
                  <span className="text-warning mt-1">→</span>
                  <p className="small mb-0">
                    Make payments through bank accounts only
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-warning mt-1">→</span>
                  <p className="small mb-0">
                    Never send cash directly or through remittance services
                  </p>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <span className="text-warning mt-1">→</span>
                  <p className="small mb-0">
                    Keep photocopies of deposit slips, cheques, or electronic transfer slips
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <span className="text-warning mt-1">→</span>
                  <p className="small mb-0">
                    Sellers: Don't dispatch goods before receiving payment
                  </p>
                </div>
              </Card.Body>
            </Card>
          </section>

          {/* Tip 6: Report Suspicious Activity */}
          <section className="mb-4">
            <Row className="mb-3">
              <Col xs="auto">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-danger">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-8a2 2 0 012-2h14a2 2 0 012 2v8" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11l7-7 7 7" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v8" />
                  </svg>
                </div>
              </Col>
              <Col>
                <h2 className="mb-3">6. Report Suspicious Activities</h2>
                <p className="mb-0">
                  Please report any fraud attempts or suspicious activities immediately.
                  In case of fraud or illegal activity, also report to local authorities.
                </p>
              </Col>
            </Row>

            <Alert variant="danger" className="border">
              <p className="fw-bold mb-2">📧 Report to:</p>
              <a
                href="mailto:support@nayabajar.com"
                className="text-danger fw-bold"
              >
                support@nayabajar.com
              </a>
            </Alert>
          </section>

          {/* Bottom Notice */}
          <div className="mt-4 pt-4 border-top">
            <p className="text-center text-muted small">
              These guidelines are not exhaustive. Always use your best judgment and stay safe!
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );

  return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false}>{content}</Layout>;
};

export default SafetyTips;
